<script>
  import { onDestroy, onMount, afterUpdate } from "svelte";
  import * as PropTypes from "prop-types";
  const { checkPropTypes, any, string, number, func } = PropTypes;

  export let rangeClass = "",
    onChange,
    onInput,
    value = 0,
    max = 100,
    min = 0,
    step = 1;

  const propTypes = {
    rangeClass: string,
    onChange: func,
    onInput: func,
    value: number,
    max: number,
    min: number,
    step: any,
  };

  let knob, rangeLeft, rangeRight, rangeSlider;
  let sliderWidth,
    knobWidth,
    leftWidth = 0;

  let mouseX;
  let maxLw = sliderWidth - knobWidth;
  const minLw = 0;

  $: if (value ?? 1) {
    const val = roundToStep(value);
    updateWidth(val);
  }

  const roundToStep = (val) => {
    if (!isNaN(step) && step > 0)
      val = min + Math.round((val - min) / step) * step;
    if (val < min) return min;
    if (val > max) return max;
    return val;
  };

  const getValue = (lw) => {
    lw = lw ?? rangeLeft?.getBoundingClientRect().width;
    const val = min + ((lw - minLw) * (max - min)) / (maxLw - minLw);
    return roundToStep(val);
  };

  const updateWidth = (val) => {
    const lw = minLw + ((val - min) * (maxLw - minLw)) / (max - min);
    const lwp = (lw * 100) / sliderWidth;
    if (rangeLeft) rangeLeft.style.width = `${lwp}%`;
  };

  const onRangeClick = (e) => {
    e.preventDefault();
    const start = rangeLeft?.getBoundingClientRect().x;
    const lw = (e.clientX || e.touches?.[0]?.clientX) - start - knobWidth / 2;
    const newVal = getValue(lw);
    updateWidth(newVal);
    onInput?.(newVal);
    onMouseDown(e);
  };

  const onDrag = (e) => {
    const dx = (e.clientX || e.touches?.[0]?.clientX) - mouseX;
    const lw = leftWidth + dx;
    const newVal = getValue(lw);
    updateWidth(newVal);
    onInput?.(newVal);
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    mouseX = e.clientX || e.touches?.[0]?.clientX;
    leftWidth = rangeLeft?.getBoundingClientRect().width;
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("touchmove", onDrag);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchend", onMouseUp);
  };

  const onMouseUp = (e) => {
    e.preventDefault();
    onChange?.(getValue());
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("touchmove", onDrag);
    document.removeEventListener("mouseup", onMouseUp);
    document.removeEventListener("touchend", onMouseUp);
  };

  onMount(() => {
    checkPropTypes(propTypes, $$props, "prop", "RangeSlider");
    if (knob) {
      knob.addEventListener("mousedown", onMouseDown);
      knob.addEventListener("touchstart", onMouseDown);
    }
    if (rangeLeft) {
      rangeLeft.addEventListener("mousedown", onRangeClick);
      rangeLeft.addEventListener("touchstart", onRangeClick);
    }
    if (rangeRight) {
      rangeRight.addEventListener("mousedown", onRangeClick);
      rangeRight.addEventListener("touchstart", onRangeClick);
    }
  });

  afterUpdate(() => {
    sliderWidth = rangeSlider?.getBoundingClientRect().width;
    knobWidth = knob?.getBoundingClientRect().width;
    maxLw = sliderWidth - knobWidth;
  });

  onDestroy(() => {
    if (knob) {
      knob.removeEventListener("mousedown", onMouseDown);
      knob.removeEventListener("touchstart", onMouseDown);
    }
    if (rangeLeft) {
      rangeLeft.removeEventListener("mousedown", onRangeClick);
      rangeLeft.removeEventListener("touchstart", onRangeClick);
    }
    if (rangeRight) {
      rangeRight.removeEventListener("mousedown", onRangeClick);
      rangeRight.removeEventListener("touchstart", onRangeClick);
    }
  });
</script>

<div
  bind:this={rangeSlider}
  class={`flex items-center w-full mx-xs ${rangeClass}`}
>
  <div
    bind:this={rangeLeft}
    class="h-2.5 cursor-pointer"
    data-testid="range-left"
  >
    <div class="h-0.5 my-1 rounded bg-white" />
  </div>
  <div
    bind:this={knob}
    data-testid="slider-knob"
    class="h-2.5 w-2.5 rounded-[100%] cursor-pointer bg-white"
  />
  <div
    bind:this={rangeRight}
    class="h-2.5 cursor-pointer grow"
    data-testid="range-right"
  >
    <div class="h-0.5 my-1 rounded bg-grey8" />
  </div>
</div>
